import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { http, formatApiError } from "./api";
import { I } from "./data";
import VidhyaWordmark from "./components/VidhyaWordmark";

// ─── Shared password input with show/hide toggle ───
function PasswordInput({ value, onChange, testid, autoFocus, minLength, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="password-wrap">
      <input
        className="input"
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoFocus={autoFocus}
        minLength={minLength}
        placeholder={placeholder}
        data-testid={testid}
      />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testid}-toggle`}
        tabIndex={-1}
      >
        {show ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
      </button>
    </div>
  );
}

// ─── Shared brand aside ───
function AuthAside({ eyebrow, headline, quote }) {
  return (
    <div className="auth-aside">
      <div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--clay-soft)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 18, position: "relative" }}>
          {eyebrow}
        </div>
        <div className="auth-aside-h1">{headline}</div>
      </div>
      <div className="auth-aside-quote">“{quote}”</div>
    </div>
  );
}

// ─────────────────────────── Login ───────────────────────────
export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      nav(u && u.onboarded === false ? "/onboarding" : "/app");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (forgot) {
    return <ForgotPassword onBack={() => setForgot(false)} initialEmail={email} />;
  }

  return (
    <div className="auth-shell" data-testid="login-page">
      <div className="auth-form-side">
        <div className="auth-card">
          <div className="brand" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 24 }}>
            <VidhyaWordmark size={30} />
          </div>
          <h2>Welcome <em>back</em>.</h2>
          <p className="sub">Sign in to your higher-education copilot.</p>

          {err && <div className="auth-error" data-testid="login-error">{err}</div>}

          <form onSubmit={submit}>
            <div className="auth-field">
              <label>Email</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="login-email-input" />
            </div>
            <div className="auth-field">
              <div className="row" style={{ justifyContent: "space-between", marginBottom: 4 }}>
                <label style={{ margin: 0 }}>Password</label>
                <button type="button" className="link-btn-inline" onClick={() => setForgot(true)} data-testid="forgot-password-link">
                  Forgot?
                </button>
              </div>
              <PasswordInput value={password} onChange={setPassword} testid="login-password-input" />
            </div>
            <button type="submit" className="btn btn-clay auth-cta" disabled={busy} data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="auth-switch">
            New to Vidhya? <Link to="/register" data-testid="go-to-register">Create an account</Link>
          </div>
        </div>
      </div>
      <AuthAside
        eyebrow="Higher-education copilot"
        headline={<><>Plan <strong>smarter,</strong></><br/><>apply with <em>clarity.</em></></>}
        quote="Vidhya helps high-school students plan, organize, and research their path to higher education — anywhere in the world."
      />
    </div>
  );
}

// ─────────────────────────── Register ───────────────────────────
export function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form"); // form | verify

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await http.post("/auth/register", { name, email, password });
      setStage("verify");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  if (stage === "verify") {
    return <VerifyEmailStep email={email} name={name} password={password} onBack={() => setStage("form")} />;
  }

  return (
    <div className="auth-shell" data-testid="register-page">
      <div className="auth-form-side">
        <div className="auth-card">
          <div className="brand" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 24 }}>
            <VidhyaWordmark size={30} />
          </div>
          <h2>Begin your <em>shortlist.</em></h2>
          <p className="sub">Create an account — your AI advisor is waiting.</p>

          {err && <div className="auth-error" data-testid="register-error">{err}</div>}

          <form onSubmit={submit}>
            <div className="auth-field">
              <label>Name</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
            </div>
            <div className="auth-field">
              <label>Email</label>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
            </div>
            <div className="auth-field">
              <label>Password (min 6)</label>
              <PasswordInput value={password} onChange={setPassword} testid="register-password-input" minLength={6} />
            </div>
            <button type="submit" className="btn btn-clay auth-cta" disabled={busy} data-testid="register-submit-btn">
              {busy ? "Sending code…" : "Create account"}
            </button>
            <p className="auth-legal" data-testid="register-legal-note">
              By creating an account you agree to our{" "}
              <Link to="/terms" data-testid="register-terms-link">Terms</Link> and{" "}
              <Link to="/privacy" data-testid="register-privacy-link">Privacy Policy</Link>.
            </p>
          </form>

          <div className="auth-switch">
            Already have one? <Link to="/login" data-testid="go-to-login">Sign in</Link>
          </div>
        </div>
      </div>
      <AuthAside
        eyebrow="Built with real counselors"
        headline={<><em>Real</em> data,<br/><strong>real</strong> deadlines,<br/><strong>real</strong> professors.</>}
        quote="We pull live data from university pages and faculty directories, so your shortlist isn't built on stale Reddit threads."
      />
    </div>
  );
}

// ─── Email verification (post-register) ───
function VerifyEmailStep({ email, name, password, onBack }) {
  const { setUser, setToken } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState(`We sent a 6-digit code to ${email}. Enter it below to finish setting up your account.`);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await http.post("/auth/verify-email", { email, code });
      // Issue token like a normal login
      localStorage.setItem("sa_token", data.token);
      setToken(data.token);
      setUser(data.user);
      nav("/onboarding");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr(""); setInfo(""); setResending(true);
    try {
      await http.post("/auth/resend-verification", { email });
      setInfo(`A fresh code is on the way to ${email}.`);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="verify-email-page">
      <div className="auth-form-side">
        <div className="auth-card">
          <div className="brand" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 24 }}>
            <VidhyaWordmark size={30} />
          </div>
          <h2>Check your <em>inbox</em>.</h2>
          {info && <p className="sub" data-testid="verify-info">{info}</p>}
          {err && <div className="auth-error" data-testid="verify-error">{err}</div>}

          <form onSubmit={submit}>
            <div className="auth-field">
              <label>6-digit code</label>
              <input
                className="input code-input"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
                data-testid="verify-code-input"
              />
            </div>
            <button type="submit" className="btn btn-clay auth-cta" disabled={busy || code.length !== 6} data-testid="verify-submit-btn">
              {busy ? "Verifying…" : "Verify & continue"}
            </button>
          </form>

          <div className="auth-switch" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button type="button" className="link-btn-inline" onClick={onBack} data-testid="verify-back">
              ← Wrong email?
            </button>
            <button type="button" className="link-btn-inline" onClick={resend} disabled={resending} data-testid="verify-resend">
              {resending ? "Sending…" : "Resend code"}
            </button>
          </div>
        </div>
      </div>
      <AuthAside
        eyebrow="One last step"
        headline={<>A real <em>inbox</em>,<br/>a real <strong>copilot.</strong></>}
        quote="Verifying your email keeps your shortlist, deadlines, and AI chats tied to a real address you control."
      />
    </div>
  );
}

// ─── Forgot password flow (request → reset) ───
function ForgotPassword({ initialEmail, onBack }) {
  const [stage, setStage] = useState("request"); // request | reset
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const requestReset = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await http.post("/auth/forgot-password", { email });
      setInfo(`If ${email} is on file, a 6-digit code is on its way.`);
      setStage("reset");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await http.post("/auth/reset-password", { email, code, new_password: newPwd });
      // Auto-redirect to login with a nudge.
      nav("/login", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="forgot-password-page">
      <div className="auth-form-side">
        <div className="auth-card">
          <div className="brand" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 24 }}>
            <VidhyaWordmark size={30} />
          </div>
          <h2>Reset your <em>password</em>.</h2>
          <p className="sub">{stage === "request"
            ? "We'll send a 6-digit code to your email to verify it's you."
            : "Enter the code we sent + your new password."}</p>
          {info && <div className="auth-info" data-testid="forgot-info">{info}</div>}
          {err && <div className="auth-error" data-testid="forgot-error">{err}</div>}

          {stage === "request" ? (
            <form onSubmit={requestReset}>
              <div className="auth-field">
                <label>Email</label>
                <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="forgot-email-input" />
              </div>
              <button type="submit" className="btn btn-clay auth-cta" disabled={busy} data-testid="forgot-submit-btn">
                {busy ? "Sending…" : "Send code"}
              </button>
            </form>
          ) : (
            <form onSubmit={submitReset}>
              <div className="auth-field">
                <label>6-digit code</label>
                <input
                  className="input code-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  data-testid="reset-code-input"
                />
              </div>
              <div className="auth-field">
                <label>New password (min 6)</label>
                <PasswordInput value={newPwd} onChange={setNewPwd} testid="reset-new-password-input" minLength={6} />
              </div>
              <button type="submit" className="btn btn-clay auth-cta" disabled={busy || code.length !== 6} data-testid="reset-submit-btn">
                {busy ? "Resetting…" : "Reset password"}
              </button>
            </form>
          )}

          <div className="auth-switch">
            <button type="button" className="link-btn-inline" onClick={onBack} data-testid="forgot-back">
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
      <AuthAside
        eyebrow="Account recovery"
        headline={<>A fresh <em>start</em>,<br/>not a lost <strong>account.</strong></>}
        quote="Your saved shortlist, chats, and deadlines all stay intact — only the password changes."
      />
    </div>
  );
}
